// ── TransactionSummaryCard ───────────────────────────────────────────────────
// Left-column summary fields on Transaction Detail.

import type { ReactNode } from "react";
import { ExternalLink } from "lucide-react";
import type { Transaction } from "@/application/money/types";
import { formatAbsolute } from "@/application/money/types";
import { chargeUrl, paymentIntentUrl } from "@/lib/stripe-links";
import { CopyButton } from "./CopyButton";

function MetaRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1 border-b py-2.5 last:border-b-0">
      <span className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
        {label}
      </span>
      <span className="text-foreground text-sm break-words">{children}</span>
    </div>
  );
}

function NullMeta() {
  return <span className="text-subtle-foreground">—</span>;
}

interface TransactionSummaryCardProps {
  transaction: Transaction;
}

export function TransactionSummaryCard({
  transaction: tx,
}: TransactionSummaryCardProps) {
  const stripeOpts = {
    chargeType: tx.chargeType,
    sourceAccount: tx.sourceAccount,
    currency: tx.currency,
  };
  const chargeLink = tx.sourceChargeId
    ? chargeUrl(tx.sourceChargeId, stripeOpts)
    : null;

  const piLink =
    tx.sourceId && tx.sourceId.startsWith("pi_")
      ? paymentIntentUrl(tx.sourceId, stripeOpts)
      : null;

  const cardDisplay =
    tx.paymentMethod?.brand && tx.paymentMethod?.last_four
      ? `${tx.paymentMethod.brand} ····${tx.paymentMethod.last_four}`
      : tx.cardLast4
        ? `····${tx.cardLast4}`
        : null;

  return (
    <div className="bg-card rounded-lg border p-5">
      <h2 className="text-foreground mb-3 text-sm font-semibold">Summary</h2>
      <div className="divide-y">
        <MetaRow label="Date">
          {tx.created != null ? (
            <span>{formatAbsolute(tx.created)}</span>
          ) : (
            <NullMeta />
          )}
        </MetaRow>

        <MetaRow label="Stripe Charge ID">
          {tx.sourceChargeId ? (
            <span className="flex items-center gap-1">
              <span className="font-mono text-xs">{tx.sourceChargeId}</span>
              <CopyButton value={tx.sourceChargeId} />
              {chargeLink && (
                <a
                  href={chargeLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800"
                  aria-label="Open in Stripe"
                >
                  <ExternalLink className="h-3 w-3" />
                </a>
              )}
            </span>
          ) : (
            <NullMeta />
          )}
        </MetaRow>

        <MetaRow label="Payment Intent ID">
          {tx.sourceId && tx.sourceId.startsWith("pi_") ? (
            <span className="flex items-center gap-1">
              <span className="font-mono text-xs">{tx.sourceId}</span>
              <CopyButton value={tx.sourceId} />
              {piLink && (
                <a
                  href={piLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800"
                  aria-label="Open in Stripe"
                >
                  <ExternalLink className="h-3 w-3" />
                </a>
              )}
            </span>
          ) : (
            <NullMeta />
          )}
        </MetaRow>

        <MetaRow label="Charge Type">{tx.chargeType ?? <NullMeta />}</MetaRow>

        <MetaRow label="Entry Mode">{tx.entryMode ?? <NullMeta />}</MetaRow>

        <MetaRow label="Card">{cardDisplay ?? <NullMeta />}</MetaRow>

        <MetaRow label="Staff">
          {tx.staffName ?? tx.staffId ?? <NullMeta />}
        </MetaRow>

        <MetaRow label="Terminal">
          {tx.terminalName ?? tx.terminalId ?? <NullMeta />}
        </MetaRow>

        {tx.description && (
          <MetaRow label="Description">{tx.description}</MetaRow>
        )}

        {tx.userInvoiceNumber && (
          <MetaRow label="Invoice #">{tx.userInvoiceNumber}</MetaRow>
        )}
      </div>
    </div>
  );
}
